import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {SAND_STREAMERS_KIND} from '../../../configs/biomeEffects/sandStreamers';
import {resolveSpawnBox} from './biomeContext';
import {
  TWO_PI,
  pickPointInBox,
  randomIn,
  randomInt,
  resolveOvalFade,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ПЕСЧАНЫЕ ЗМЕЙКИ (пустыни). Чистые функции без Phaser и DOM: по этому описанию
 * частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * Порыв — полоса из нескольких лент песка, которая проходит через песчаное
 * море подложки от кромки до кромки: рождается, когда первая лента подходит к
 * песку, и живет, пока с него не уйдет последняя.
 *
 * Лента — цепочка мягких светящихся штрихов, уложенных внахлест вдоль своего
 * русла. Русло — волна поперек пути, НЕПОДВИЖНАЯ в пространстве: место штриха
 * поперек пути зависит только от того, где он сейчас вдоль пути. Поэтому лента
 * не едет жесткой загогулиной, а течет по извилистой дорожке, будто обтекая
 * барханы.
 *
 * Сторону, в которую несет песок, задает ветер матча (тот же, что несет
 * облака), наклон пути — подложка: он подобран под нарисованные на ней гребни
 * барханов и пыльные разводы.
 *
 * Ленты видны только на песчаном море: у его кромки гаснут — на скалах,
 * оазисах и воде вокруг их нет.
 *
 * Дорожка порывов помечена (SAND_STREAMERS_TAG): по метке сцена находит идущий
 * порыв и, когда он в кадре игрока, играет шум ветра над песком (см.
 * scenes/FieldEffectSounds). Рождаться порывам метка не мешает: сцена их не
 * придерживает.
 */

/** Метка дорожки песчаных змеек: по ней сцена находит идущий порыв */
export const SAND_STREAMERS_TAG = 'sandStreamers';

/** Яркость вдоль ленты: 0 — хвост, 1 — голова; концы сходят на нет, голова ярче */
const resolveBodyProfile = (bodyShare, {taperPower, tailShare}) => (
  Math.sin(Math.PI * bodyShare) ** taperPower * (tailShare + (1 - tailShare) * bodyShare)
);

const createTracks = (context, params) => {
  const {hexSize, wind} = context;
  const {streamers} = params;
  const tiltRad = toRadians(params.headingDeg);
  // Вправо или влево — по ветру матча, наклон — по рисунку подложки
  const heading = {x: Math.cos(tiltRad) * (wind.x < 0 ? -1 : 1), y: Math.sin(tiltRad)};
  const across = {x: -heading.y, y: heading.x};
  const plain = context.toWorldOval(params.plain);
  // Полуразмер песчаного моря вдоль пути порыва
  const plainReach = Math.hypot(plain.radiusX * heading.x, plain.radiusY * heading.y);

  /** Отклонение русла ленты поперек пути в данном месте пути: волна, неподвижная в пространстве */
  const resolveLaneOffset = (streamer, along) => (
    streamer.lane + streamer.waveAmplitude * Math.sin((TWO_PI * along) / streamer.waveLength + streamer.wavePhase)
  );

  /**
   * Где русло ленты идет по песку: {enter, leave} — места вдоль пути, между
   * которыми лента бывает видна; null — русло проходит мимо песчаного моря.
   * Ищется пробами вдоль русла: у извилистого русла на овале формулы нет, а
   * порыв рождается раз в десяток секунд
   */
  const resolveLaneSpan = (through, streamer, from, to) => {
    const step = hexSize * params.probeStepFactor;
    let enter = null;
    let leave = null;
    for (let along = from; along <= to; along += step) {
      const offset = resolveLaneOffset(streamer, along);
      const x = through.x + heading.x * along + across.x * offset;
      const y = through.y + heading.y * along + across.y * offset;
      if (resolveOvalFade(plain, x, y, params.plainFade) > 0) {
        enter = enter ?? along - step;
        leave = along + step;
      }
    }
    return enter === null ? null : {enter, leave};
  };

  const buildGroup = ({rng, view}) => {
    // Точка поля, через которую пройдет середина порыва: там, куда смотрит камера
    const through = pickPointInBox(rng, resolveSpawnBox(context, view, {fieldMarginFactor: 0, viewMarginFactor: 0}));
    // Камера смотрит за кромку песчаного моря: порыв прошел бы невидимым —
    // очередь пропускается, следующая попытка возьмет другую точку
    if (!(resolveOvalFade(plain, through.x, through.y, params.plainFade) > 0)) {
      return null;
    }
    // Самый длинный путь вдоль ветра — от кромки песчаного моря до кромки
    const plainAlong = (plain.x - through.x) * heading.x + (plain.y - through.y) * heading.y;
    const speed = hexSize * randomIn(rng, params.speedFactor);

    const count = randomInt(rng, streamers.count.min, streamers.count.max);
    // Дорожки лент поперек пути: соседние — на случайном расстоянии друг от друга
    const lanes = [0];
    for (let index = 1; index < count; index += 1) {
      lanes.push(lanes[index - 1] + hexSize * randomIn(rng, streamers.laneGapFactor));
    }
    const bandWidth = lanes[count - 1];

    const described = [];
    for (let index = 0; index < count; index += 1) {
      const length = hexSize * randomIn(rng, streamers.lengthFactor);
      const streamer = {
        layer: FIELD_BIOME_LAYER_KEYS.GROUND_GLOW,
        texture: FIELD_BIOME_TEXTURES.STREAK,
        tint: params.tint,
        streamerIndex: index,
        lane: lanes[index] - bandWidth / 2,
        lag: hexSize * randomIn(rng, streamers.lagFactor),
        length,
        segmentLength: (length / streamers.segmentCount) * streamers.segmentOverlap,
        thickness: hexSize * randomIn(rng, streamers.thicknessFactor),
        speed: speed * randomIn(rng, streamers.speedShare),
        alpha: params.alpha * randomIn(rng, streamers.alphaShare),
        waveAmplitude: hexSize * randomIn(rng, streamers.waveAmplitudeFactor),
        waveLength: hexSize * randomIn(rng, streamers.waveLengthFactor),
        wavePhase: rng() * TWO_PI,
        pulsePeriodMs: randomIn(rng, streamers.pulsePeriodMs),
        pulsePhase: rng() * TWO_PI,
      };
      const span = resolveLaneSpan(through, streamer, plainAlong - plainReach, plainAlong + plainReach);
      // Русло идет мимо песка: такую ленту не увидеть ни разу — ее и нет
      if (span) {
        described.push({...streamer, ...span});
      }
    }
    if (described.length === 0) {
      return null;
    }

    // Порыв рождается в тот миг, когда первая лента подходит к песку (у каждой —
    // свое отставание от фронта), и живет, пока хвост последней с него не уйдет:
    // пока порыв жив, следующий не родится, и невидимый путь был бы простоем
    const from = Math.min(...described.map(({enter, lag}) => enter + lag));
    const particles = [];
    let durationMs = 0;
    described.forEach((streamer) => {
      durationMs = Math.max(
        durationMs, ((streamer.leave - from + streamer.lag + streamer.length) / streamer.speed) * 1000
      );
      for (let segment = 0; segment < streamers.segmentCount; segment += 1) {
        // Место штриха в ленте: 0 — хвост, 1 — голова
        const bodyShare = (segment + 0.5) / streamers.segmentCount;
        particles.push({
          ...streamer,
          bodyShare,
          // Яркость и толщина штриха на его месте в ленте: к хвосту лента бледнее и тоньше
          segmentAlpha: streamer.alpha * resolveBodyProfile(bodyShare, streamers),
          segmentThickness: streamer.thickness
            * (streamers.tailThicknessShare + (1 - streamers.tailThicknessShare) * bodyShare),
        });
      }
    });

    return {durationMs, through, from, speed, particles};
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    if (!(elapsedMs > 0) || !(elapsedMs < group.durationMs)) {
      return writeHiddenPose(out);
    }
    // Голова ленты идет от начала пути порыва, штрих — на своем месте позади нее
    const headAlong = group.from - particle.lag + (particle.speed * elapsedMs) / 1000;
    const along = headAlong - (1 - particle.bodyShare) * particle.length;
    // Русло неподвижно: отклонение поперек пути — функция места вдоль пути
    const offset = resolveLaneOffset(particle, along);
    const x = group.through.x + heading.x * along + across.x * offset;
    const y = group.through.y + heading.y * along + across.y * offset;
    const ground = resolveOvalFade(plain, x, y, params.plainFade);
    if (!(ground > 0)) {
      return writeHiddenPose(out);
    }

    const pulse = 1 - streamers.pulseDepth
      * (0.5 + 0.5 * Math.sin((TWO_PI * elapsedMs) / particle.pulsePeriodMs + particle.pulsePhase));
    // Штрих лежит по касательной к руслу
    const slope = particle.waveAmplitude * (TWO_PI / particle.waveLength)
      * Math.cos((TWO_PI * along) / particle.waveLength + particle.wavePhase);
    return writePose(
      out, x, y, particle.segmentAlpha * pulse * ground, particle.segmentLength, particle.segmentThickness,
      Math.atan2(heading.y + across.y * slope, heading.x + across.x * slope)
    );
  };

  return [{
    tag: SAND_STREAMERS_TAG,
    schedule: {firstDelayMs: params.firstDelayMs, intervalMs: params.intervalMs, maxGroups: 1},
    buildGroup,
    resolvePose,
  }];
};

const sandStreamers = {
  kind: SAND_STREAMERS_KIND,
  textures: [FIELD_BIOME_TEXTURES.STREAK],
  createTracks,
};

export default sandStreamers;
